/**
 * Panel layout validation and template definitions for MegaMenu.
 *
 * Implements the constraint matrix from Decision 7 (07-panel-layout-templates):
 * - 11 valid column configurations
 * - 8 named templates (4 launch, 4 post-launch)
 * - Layout validation with descriptive error reasons
 *
 * @see https://pippin.sara.amazon.dev/architect/mega_menu_cms_authoring?artifact=07panellayouttemplates
 */

// ── Types ────────────────────────────────────────────────────────────────

/** Column types supported in a panel */
export type ColumnType = 'single' | 'dual' | 'featured';

/** Visual variants for single/dual columns. Featured columns have no variant. */
export type ColumnVariant = 'with-icons' | 'without-icons' | 'icon-only';

/** A column position within a template (undefined = empty slot) */
export type ColumnSlot = ColumnType | undefined;

/** Panel template definition */
export interface PanelTemplate {
  /** Unique template identifier */
  id: string;
  /** Human-readable name */
  name: string;
  /** Column positions left-to-right. Array index = position. */
  positions: [ColumnSlot, ColumnSlot, ColumnSlot];
  /** Whether this template is available at launch */
  isLaunch: boolean;
}

/** Result of panel layout validation */
export interface ValidationResult {
  valid: boolean;
  reason?: string;
}

// ── Template Definitions ─────────────────────────────────────────────────

/** All 8 named panel templates. Array index = column position (left to right). */
export const PANEL_TEMPLATES: readonly PanelTemplate[] = [
  // Launch templates (all include featured in pos 3)
  { id: 'featured-single', name: 'Featured + Single', positions: [undefined, 'single', 'featured'], isLaunch: true },
  { id: 'featured-dual', name: 'Featured + Dual', positions: [undefined, 'dual', 'featured'], isLaunch: true },
  { id: 'featured-single-single', name: 'Featured + Two Singles', positions: ['single', 'single', 'featured'], isLaunch: true },
  { id: 'featured-single-dual', name: 'Featured + Single + Dual', positions: ['single', 'dual', 'featured'], isLaunch: true },
  // Post-launch templates
  { id: 'dual-dual', name: 'Two Duals', positions: ['dual', 'dual', undefined], isLaunch: false },
  { id: 'triple-single', name: 'Three Singles', positions: ['single', 'single', 'single'], isLaunch: false },
  { id: 'single', name: 'Single Column', positions: ['single', undefined, undefined], isLaunch: false },
  { id: 'dual-single', name: 'Dual + Single', positions: ['dual', 'single', undefined], isLaunch: false },
] as const;

export const LAUNCH_TEMPLATES = PANEL_TEMPLATES.filter(t => t.isLaunch);
export const POST_LAUNCH_TEMPLATES = PANEL_TEMPLATES.filter(t => !t.isLaunch);

// ── Lookup ───────────────────────────────────────────────────────────────

/** Look up a template by ID. Returns undefined if not found. */
export function getTemplateById(templateId: string): PanelTemplate | undefined {
  return PANEL_TEMPLATES.find(t => t.id === templateId);
}

// ── Validation ───────────────────────────────────────────────────────────

/**
 * Validate a panel layout against the constraint matrix.
 *
 * Rules:
 * - Max 2 duals, max 1 featured per panel
 * - 2 duals → no singles, no featured
 * - 1 dual → 0-1 singles, 0-1 featured
 * - 0 duals + 0 featured → max 3 singles
 * - 0 duals + 1 featured → max 2 singles
 * - Empty panel not allowed
 * - icon-only variant only valid on single columns
 */
export function validatePanelLayout(
  columns: Array<{ type: ColumnType; variant?: ColumnVariant }>,
): ValidationResult {
  if (columns.length === 0) {
    return { valid: false, reason: 'Empty panel — at least one column required' };
  }

  const duals = columns.filter(c => c.type === 'dual').length;
  const singles = columns.filter(c => c.type === 'single').length;
  const featured = columns.filter(c => c.type === 'featured').length;

  if (featured > 1) {
    return { valid: false, reason: 'Max one featured column per panel' };
  }

  if (duals > 2) {
    return { valid: false, reason: 'Max two dual columns per panel' };
  }

  if (duals === 2 && (singles > 0 || featured > 0)) {
    return { valid: false, reason: 'Two dual columns leaves no room for other columns' };
  }

  if (duals === 1 && singles > 1) {
    return { valid: false, reason: 'One dual column allows at most one single column' };
  }

  const maxSingles = featured === 1 ? 2 : 3;
  if (duals === 0 && singles > maxSingles) {
    return {
      valid: false,
      reason: featured === 1
        ? 'With a featured column, max two single columns are allowed'
        : 'Max three single columns per panel',
    };
  }

  // Variant validation: icon-only restricted to single columns
  for (const col of columns) {
    if (col.variant === 'icon-only' && col.type !== 'single') {
      return { valid: false, reason: `icon-only variant is only valid on single columns, found on ${col.type}` };
    }
  }

  return { valid: true };
}
